import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import { AppointmentRecord } from "~/api/API";
import ReportGenerationModal, {
  SectionOptions,
} from "../pages/components/general/modals/ReportGenerationModal";

interface ReportGenerationOptions {
  appointment: AppointmentRecord;
  doctorName: string;
  onConfirm: (options: SectionOptions) => Promise<void>;
  // We might add more options later if needed, e.g., triggerType
}

interface ReportGenerationContextType {
  showReportGenerationOptions: (options: ReportGenerationOptions) => void;
}

const ReportGenerationContext = createContext<
  ReportGenerationContextType | undefined
>(undefined);

export function ReportGenerationProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [modalData, setModalData] = useState<ReportGenerationOptions | null>(
    null,
  );

  const showReportGenerationOptions = useCallback(
    (options: ReportGenerationOptions) => {
      setModalData(options);
      setIsOpen(true);
    },
    [],
  );

  const handleConfirm = useCallback(
    async (options: SectionOptions) => {
      if (modalData?.onConfirm) {
        await modalData.onConfirm(options);
      }
      setIsOpen(false);
      setModalData(null); // Clear data after confirm
    },
    [modalData],
  );

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    setModalData(null); // Clear data on cancel
  }, []);

  return (
    <ReportGenerationContext.Provider value={{ showReportGenerationOptions }}>
      {children}
      {modalData && ( // Render modal only when data is available
        <ReportGenerationModal
          isOpen={isOpen}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          appointment={modalData.appointment}
          doctorName={modalData.doctorName}
        />
      )}
    </ReportGenerationContext.Provider>
  );
}

export const useReportGeneration = () => {
  const context = useContext(ReportGenerationContext);
  if (!context) {
    throw new Error(
      "useReportGeneration must be used within a ReportGenerationProvider",
    );
  }
  return context;
};
